import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { H4, Body } from '@meiuca/ui-components'
import { Dumbbell, Loader2 } from 'lucide-react'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { WorkoutCard } from '@/components/WorkoutCard'
import { StudentFilterPills, buildStudentFilterOptions, type StudentFilterOption } from '@/components/StudentFilterPills'
import { useAuth } from '@/contexts/AuthContext'
import { LoginRequiredEmptyState } from '@/components/LoginRequiredEmptyState'
import {
  workoutService,
  type WorkoutListItem
} from '@/services/workoutService'
import type { PaginationInfo } from '@/services/pagination'

type TabType = 'upcoming' | 'past'

export function WorkoutsPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()

  const [activeTab, setActiveTab] = useState<TabType>('upcoming')
  const [workouts, setWorkouts] = useState<WorkoutListItem[]>([])
  const [pagination, setPagination] = useState<PaginationInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Filtro por pessoa (responsável com dependentes). Server-side por causa do
  // cursor; as opções derivam da última página sem filtro e ficam de pé
  // enquanto um filtro está ativo.
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null)
  const [studentOptions, setStudentOptions] = useState<StudentFilterOption[]>([])

  useEffect(() => {
    analyticsService.trackScreenView(SCREEN_NAMES.WORKOUTS)
  }, [])

  useEffect(() => {
    // Show the login empty state when logged out instead of redirecting away.
    if (isAuthenticated) {
      loadWorkouts(selectedStudentId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAuthenticated, selectedStudentId])

  const loadWorkouts = async (studentCustomerId: string | null) => {
    try {
      setLoading(true)
      setError(null)
      const response = await workoutService.listWorkouts({
        period: activeTab,
        limit: 20,
        studentCustomerId: studentCustomerId ?? undefined
      })
      setWorkouts(response.data)
      setPagination(response.pagination)
      if (!studentCustomerId) {
        setStudentOptions(buildStudentFilterOptions(response.data))
      }
    } catch (err) {
      console.error('Error loading workouts:', err)
      setError('Erro ao carregar seus treinos. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const loadMore = async () => {
    if (!pagination?.hasMore || loadingMore) return
    try {
      setLoadingMore(true)
      setError(null)
      const response = await workoutService.listWorkouts({
        period: activeTab,
        limit: 20,
        cursor: pagination.cursor ?? undefined,
        studentCustomerId: selectedStudentId ?? undefined
      })
      setWorkouts((prev) => [...prev, ...response.data])
      setPagination(response.pagination)
    } catch (err) {
      console.error('Error loading more workouts:', err)
      setError('Erro ao carregar mais treinos. Tente novamente.')
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <H4 className="text-text-primary font-semibold mb-2">Meus Treinos</H4>
          <Body className="text-text-secondary">Acompanhe suas aulas do centro de treinamento</Body>
        </div>

        <div className="flex gap-2 mb-6 border-b border-border-light">
          <button
            onClick={() => {
              analyticsService.track(ANALYTICS_EVENTS.WORKOUTS_TAB_CHANGED, { tab: 'upcoming' })
              setActiveTab('upcoming')
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'upcoming'
                ? 'text-brand-primary border-b-2 border-brand-primary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Próximos
          </button>
          <button
            onClick={() => {
              analyticsService.track(ANALYTICS_EVENTS.WORKOUTS_TAB_CHANGED, { tab: 'past' })
              setActiveTab('past')
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'past'
                ? 'text-brand-primary border-b-2 border-brand-primary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Anteriores
          </button>
        </div>

        {/* Fora da cascata de estados pra sobreviver ao vazio de um filtro. */}
        {isAuthenticated && (
          <StudentFilterPills
            options={studentOptions}
            selectedId={selectedStudentId}
            onSelect={id => {
              setSelectedStudentId(id)
              analyticsService.track(ANALYTICS_EVENTS.WORKOUTS_STUDENT_FILTER_CHANGED, {
                student_customer_id: id ?? 'all'
              })
            }}
          />
        )}

        {isAuthLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
          </div>
        ) : !isAuthenticated ? (
          <LoginRequiredEmptyState
            title="Entre para ver seus treinos"
            description="Faça login para acompanhar suas aulas do centro de treinamento."
          />
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
            <Body className="text-text-secondary mt-4">Carregando treinos...</Body>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Body className="text-feedback-error mb-4">{error}</Body>
            <button
              onClick={() => loadWorkouts(selectedStudentId)}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        ) : workouts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 mb-4 rounded-full bg-background-secondary flex items-center justify-center">
              <Dumbbell className="w-8 h-8 text-text-tertiary" />
            </div>
            <H4 className="text-text-primary mb-2">Nenhum treino encontrado</H4>
            <Body className="text-text-secondary text-center max-w-md">
              {activeTab === 'upcoming'
                ? 'Você não tem treinos agendados no momento.'
                : 'Você ainda não tem histórico de treinos.'}
            </Body>
          </div>
        ) : (
          <div className="space-y-4">
            {workouts.map((workout, index) => (
              <WorkoutCard
                key={`${workout.id}-${index}`}
                workout={workout}
                position={index}
                showStudent={studentOptions.length > 0}
                onClick={() => {
                  analyticsService.track(ANALYTICS_EVENTS.WORKOUT_CARD_CLICKED, { workout_id: workout.id, position: index })
                  navigate(`/treinos/${workout.id}`)
                }}
              />
            ))}

            {pagination?.hasMore && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-6 py-3 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Carregando...
                    </>
                  ) : (
                    'Carregar mais'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
